import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error, expired
  const [message, setMessage] = useState('Verifico lo stato del pagamento...');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const maxAttempts = 10;
  const pollInterval = 2000; // 2 seconds

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Sessione di pagamento non trovata');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollAttempts >= maxAttempts) {
      setStatus('error');
      setMessage('Timeout nella verifica del pagamento. Controlla la tua email per la conferma.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout-status/${sessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Errore nella verifica del pagamento');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid' || data.status === 'complete') {
        setStatus('success');
        setMessage('Pagamento completato con successo!');
        setPaymentDetails(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        setMessage('La sessione di pagamento è scaduta. Riprova.');
        return;
      }

      // Continue polling if still pending
      setMessage('Sto verificando il pagamento...');
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);

    } catch (error) {
      console.error('Errore nel controllo dello stato:', error);
      
      // Retry on error
      if (pollAttempts < maxAttempts) {
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus('error');
        setMessage('Errore nella verifica del pagamento. Contatta il supporto.');
      }
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center px-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8">
          <div className="text-center">
            {status === 'checking' && (
              <div className="space-y-4">
                <Loader2 className="w-16 h-16 text-purple-600 animate-spin mx-auto" />
                <h2 className="text-2xl font-bold text-gray-900">Verifica Pagamento</h2>
                <p className="text-gray-600">{message}</p>
                <div className="text-sm text-gray-500">
                  Tentativo {pollAttempts + 1} di {maxAttempts}
                </div>
              </div>
            )}

            {status === 'success' && (
              <div className="space-y-4">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
                <h2 className="text-2xl font-bold text-gray-900">Pagamento Confermato!</h2>
                <p className="text-gray-600">{message}</p>
                
                {paymentDetails && (
                  <div className="bg-green-50 rounded-lg p-4 mt-4 text-left">
                    <h3 className="font-semibold text-green-900 mb-2">Dettagli Pagamento:</h3>
                    <div className="space-y-1 text-sm text-green-800">
                      <p><span className="font-medium">Importo:</span> €{(paymentDetails.amount_total / 100).toFixed(2)}</p>
                      <p><span className="font-medium">Valuta:</span> {paymentDetails.currency?.toUpperCase()}</p>
                      <p><span className="font-medium">Piano:</span> {paymentDetails.metadata?.package_name}</p>
                    </div>
                  </div>
                )}
                
                <div className="pt-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Il tuo abbonamento è stato attivato con successo. Riceverai una email di conferma a breve.
                  </p>
                  <Button onClick={handleContinue} className="w-full bg-purple-600 hover:bg-purple-700">
                    Vai alla Dashboard
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-4">
                <XCircle className="w-16 h-16 text-red-600 mx-auto" />
                <h2 className="text-2xl font-bold text-gray-900">Errore nel Pagamento</h2>
                <p className="text-gray-600">{message}</p>
                
                <div className="pt-4 space-y-2">
                  <Button onClick={() => navigate('/pricing')} className="w-full bg-purple-600 hover:bg-purple-700">
                    Riprova
                  </Button>
                  <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
                    Torna alla Dashboard
                  </Button>
                </div>
              </div>
            )}

            {status === 'expired' && (
              <div className="space-y-4">
                <XCircle className="w-16 h-16 text-orange-600 mx-auto" />
                <h2 className="text-2xl font-bold text-gray-900">Sessione Scaduta</h2>
                <p className="text-gray-600">{message}</p>
                
                <div className="pt-4">
                  <Button onClick={() => navigate('/pricing')} className="w-full bg-purple-600 hover:bg-purple-700">
                    Riprova il Pagamento
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
